/**
 * HotelsTab.jsx — 호텔 list + CRUD (Phase 2 조회 + Phase 3-C 수정/추가/삭제, 2026-04-30)
 */
import React, { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import HotelModal from './HotelModal';
import {
  mobileActionRow,
  mobileAddButton,
  mobileAmount,
  mobileCard,
  mobileCardHeader,
  mobileCardList,
  mobileCardTitle,
  mobileDeleteButton,
  mobileEditButton,
  mobileEmpty,
  mobileInfoGrid,
  mobileInfoItem,
  mobileInfoLabel,
  mobileInfoValue,
  mobileSubText,
  mobileTabWrap,
  mobileTitleText,
  mobileTopActionRow,
} from './ReservationDetail.mobileStyles';

const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString('ko-KR'));
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '-');

function nightlyToText(rates) {
  if (!rates || (Array.isArray(rates) && rates.length === 0)) return '-';
  return (Array.isArray(rates) ? rates : []).map((r) => `${r.night}박:${fmt(r.amount)}`).join(' / ') || '-';
}

export default function HotelsTab({ rows = [], travelCode, onChanged, isMobile = false }) {
  const [modalRow, setModalRow] = useState(undefined);

  async function handleDelete(row) {
    if (!window.confirm(`호텔 #${row.id} 삭제하시겠습니까?`)) return;
    const r = await apiClient.delete(`/api/admin/hotel-bookings/${row.id}`);
    if (r.ok && r.data?.success) onChanged && onChanged();
    else alert(r.data?.message || '삭제 실패');
  }

  return (
    <div style={isMobile ? mobileTabWrap : { display: 'grid', gap: 8 }}>
      <div style={isMobile ? mobileTopActionRow : { display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => setModalRow(null)} style={isMobile ? mobileAddButton : addBtn}>+ 호텔 추가</button>
      </div>
      {rows.length === 0 ? (
        <div style={isMobile ? mobileEmpty : emptyStyle}>호텔 예약 없음</div>
      ) : isMobile ? (
        <div style={mobileCardList}>
          {rows.map((r, index) => (
            <div key={r.id} style={mobileCard}>
              <div style={mobileCardHeader}>
                <div style={mobileCardTitle}>
                  <span style={mobileTitleText}>호텔 #{index + 1}</span>
                  <span style={mobileSubText}>{r.hotel_name || r.room_type || '-'}</span>
                </div>
                <span style={mobileAmount()}>{fmt(r.total_cost)}</span>
              </div>
              <div style={mobileInfoGrid}>
                <Info label="호텔명" value={r.hotel_name || '-'} />
                <Info label="객실" value={`${r.room_type || '-'}${r.room_count > 1 ? ` × ${r.room_count}` : ''}`} />
                <Info label="체크인" value={fmtDate(r.check_in)} />
                <Info label="체크아웃" value={fmtDate(r.check_out)} />
                <Info label="박/인원" value={`${r.nights || '-'}박 / ${r.guest_count || '-'}명`} />
                <Info label="일별요금" value={nightlyToText(r.nightly_rates)} />
              </div>
              <div style={mobileActionRow}>
                <button onClick={() => setModalRow(r)} style={mobileEditButton}>수정</button>
                <button onClick={() => handleDelete(r)} style={mobileDeleteButton}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
              <tr>
                <th style={th}>호텔명</th>
                <th style={th}>객실</th>
                <th style={th}>체크인</th>
                <th style={th}>체크아웃</th>
                <th style={th}>박/인원</th>
                <th style={th}>일별요금</th>
                <th style={{ ...th, textAlign: 'right' }}>총액</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}>{r.hotel_name || '-'}</td>
                  <td style={td}>{r.room_type || '-'}{r.room_count > 1 ? ` × ${r.room_count}` : ''}</td>
                  <td style={td}>{fmtDate(r.check_in)}</td>
                  <td style={td}>{fmtDate(r.check_out)}</td>
                  <td style={td}>{r.nights || '-'}박 / {r.guest_count || '-'}명</td>
                  <td style={td}>{nightlyToText(r.nightly_rates)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(r.total_cost)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModalRow(r)} style={editBtn}>수정</button>{' '}
                    <button onClick={() => handleDelete(r)} style={delBtn}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modalRow !== undefined && (
        <HotelModal travelCode={travelCode} row={modalRow}
          onClose={() => setModalRow(undefined)}
          onSaved={() => onChanged && onChanged()} />
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={mobileInfoItem}>
      <span style={mobileInfoLabel}>{label}</span>
      <span style={mobileInfoValue}>{value}</span>
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11 };
const td = { padding: '6px 10px', color: 'var(--text)' };
const emptyStyle = { padding: 16, color: 'var(--text-muted)', textAlign: 'center' };
const addBtn  = { padding: '4px 12px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const editBtn = { padding: '2px 8px', background: 'transparent', color: 'var(--accent-300, #a78bfa)', border: '1px solid var(--accent-700, #7c3aed)', borderRadius: 4, cursor: 'pointer', fontSize: 10 };
const delBtn  = { padding: '2px 8px', background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 4, cursor: 'pointer', fontSize: 10 };
